// Simplified theme property registry for API schema generation.
// Source of truth: packages/checkout/src/theming/themePropertyRegistry.ts
// TODO: Import from @repo/checkout after monorepo merge

// ============================================================================
// Types
// ============================================================================

export type ThemePropertyType =
  | 'color'
  | 'font'
  | 'fontWeight'
  | 'fontStyle'
  | 'fontSize'
  | 'size'
  | 'shadow'
  | 'alignment'
  | 'select'
  | 'toggle'
  | 'image'
  | 'text';

export interface ThemeProperty {
  path: string;
  type: ThemePropertyType;
  description: string;
  options?: OptionsRef | { value: string | number; label: string }[];
  // Values the dashboard no longer offers that saved themes still carry and the checkout still honours: a theme
  // save accepts them, the seller API does not.
  legacyValues?: (string | number)[];
  defaultValue?: unknown;
}

// A per-option price/detail line in the price picker ("Price options" element).
// Keep in sync with packages/checkout/src/theming/themePropertyRegistry.ts.
function optionLineProps(elem: string, human: string): ThemeProperty[] {
  const base = `variables.elements.product.pricePicker.option.${elem}.styles`;
  return [
    { path: `${base}.color`, type: 'color', description: `Text color of the ${human}.` },
    { path: `${base}.fontFamily`, type: 'font', description: `Font family of the ${human}.` },
    { path: `${base}.fontStyle`, type: 'fontStyle', description: `Font style of the ${human}.` },
    { path: `${base}.fontWeight`, type: 'fontWeight', description: `Font weight of the ${human}.` },
    {
      path: `${base}.fontSize`,
      type: 'fontSize',
      description: `Font size of the ${human}.`,
      options: 'fontSize' as OptionsRef,
    },
  ];
}

// Full Field > Input styling parity for a "PWYW input" element. Mirrors
// packages/checkout/src/theming/themePropertyRegistry.ts pwywInputGroup.
function pwywInputApiEntries(basePath: string): ThemeProperty[] {
  const d = 'the pay-what-you-want amount input';
  return [
    {
      path: `${basePath}.styles.backgroundColor`,
      type: 'color',
      description: `Background color of ${d}.`,
    },
    { path: `${basePath}.styles.color`, type: 'color', description: `Text color of ${d}.` },
    { path: `${basePath}.styles.borderColor`, type: 'color', description: `Border color of ${d}.` },
    {
      path: `${basePath}.styles.borderWidth`,
      type: 'size',
      description: `Border width of ${d}.`,
      options: 'borderWidth' as OptionsRef,
    },
    {
      path: `${basePath}.styles.borderRadius`,
      type: 'size',
      description: `Border radius of ${d}.`,
      options: 'sizePx' as OptionsRef,
    },
    {
      path: `${basePath}.styles.paddingX`,
      type: 'size',
      description: `Horizontal padding of ${d}.`,
      options: 'paddingPx' as OptionsRef,
    },
    {
      path: `${basePath}.styles.paddingY`,
      type: 'size',
      description: `Vertical padding of ${d}.`,
      options: 'paddingPx' as OptionsRef,
    },
    {
      path: `${basePath}.styles.boxShadow`,
      type: 'shadow',
      description: `Shadow size of ${d}.`,
      options: 'shadow' as OptionsRef,
    },
    { path: `${basePath}.styles.fontFamily`, type: 'font', description: `Font family of ${d}.` },
    { path: `${basePath}.styles.fontStyle`, type: 'fontStyle', description: `Font style of ${d}.` },
    {
      path: `${basePath}.styles.fontWeight`,
      type: 'fontWeight',
      description: `Font weight of ${d}.`,
    },
    {
      path: `${basePath}.styles.fontSize`,
      type: 'fontSize',
      description: `Font size of ${d}.`,
      options: 'fontSize' as OptionsRef,
    },
    {
      path: `${basePath}.focusStyles.color`,
      type: 'color',
      description: `Focus text color of ${d}.`,
    },
    {
      path: `${basePath}.focusStyles.backgroundColor`,
      type: 'color',
      description: `Focus background color of ${d}.`,
    },
    {
      path: `${basePath}.focusStyles.borderColor`,
      type: 'color',
      description: `Focus border color of ${d}.`,
    },
    {
      path: `${basePath}.placeholderStyles.color`,
      type: 'color',
      description: `Placeholder color of ${d}.`,
    },
  ];
}

// Full Fields > Label styling parity for a "PWYW label" element. Mirrors
// packages/checkout/src/theming/themePropertyRegistry.ts pwywLabelGroup.
function pwywLabelApiEntries(basePath: string): ThemeProperty[] {
  const d = 'the pay-what-you-want label';
  return [
    { path: `${basePath}.styles.color`, type: 'color', description: `Color of ${d}.` },
    {
      path: `${basePath}.styles.paddingBottom`,
      type: 'size',
      description: `Bottom padding below ${d}.`,
      options: 'paddingPx' as OptionsRef,
    },
    { path: `${basePath}.styles.fontFamily`, type: 'font', description: `Font family of ${d}.` },
    { path: `${basePath}.styles.fontStyle`, type: 'fontStyle', description: `Font style of ${d}.` },
    {
      path: `${basePath}.styles.fontWeight`,
      type: 'fontWeight',
      description: `Font weight of ${d}.`,
    },
    {
      path: `${basePath}.styles.fontSize`,
      type: 'fontSize',
      description: `Font size of ${d}.`,
      options: 'fontSize' as OptionsRef,
    },
    {
      path: `${basePath}.focusStyles.color`,
      type: 'color',
      description: `Color of ${d} when the input is focused.`,
    },
  ];
}

// Mirrors packages/checkout/src/theming/themePropertyRegistry.ts pwywSuffixGroup
// (the PWYW input Interval / Currency suffix): color + font, no padding/focus.
function pwywSuffixApiEntries(basePath: string): ThemeProperty[] {
  const d = 'the pay-what-you-want input suffix (interval or currency)';
  return [
    { path: `${basePath}.styles.color`, type: 'color', description: `Color of ${d}.` },
    { path: `${basePath}.styles.fontFamily`, type: 'font', description: `Font family of ${d}.` },
    { path: `${basePath}.styles.fontStyle`, type: 'fontStyle', description: `Font style of ${d}.` },
    {
      path: `${basePath}.styles.fontWeight`,
      type: 'fontWeight',
      description: `Font weight of ${d}.`,
    },
    {
      path: `${basePath}.styles.fontSize`,
      type: 'fontSize',
      description: `Font size of ${d}.`,
      options: 'fontSize' as OptionsRef,
    },
  ];
}

// Mirrors fontGroup in packages/checkout/src/theming/themePropertyRegistry.ts.
function fontProps(base: string, human: string): ThemeProperty[] {
  return [
    { path: `${base}.fontFamily`, type: 'font', description: `Font family of the ${human}.` },
    { path: `${base}.fontStyle`, type: 'fontStyle', description: `Font style of the ${human}.` },
    { path: `${base}.fontWeight`, type: 'fontWeight', description: `Font weight of the ${human}.` },
    {
      path: `${base}.fontSize`,
      type: 'fontSize',
      description: `Font size of the ${human}.`,
      options: 'fontSize' as OptionsRef,
    },
  ];
}

// ============================================================================
// Shared Option Arrays
// ============================================================================

export const THEME_OPTIONS = {
  fontSize: [
    { value: '', label: 'Auto' },
    { value: 10, label: '10px' },
    { value: 11, label: '11px' },
    { value: 12, label: '12px' },
    { value: 13, label: '13px' },
    { value: 14, label: '14px' },
    { value: 16, label: '16px' },
    { value: 18, label: '18px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
  ],
  fontStyle: [
    { value: '', label: 'Auto' },
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Italic' },
  ],
  borderWidth: [
    { value: '', label: 'Auto' },
    { value: 0, label: 'None' },
    { value: 1, label: '1px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
  ],
  borderStyle: [
    { value: '', label: 'Auto' },
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
    { value: 'dotted', label: 'Dotted' },
  ],
  shadow: [
    { value: '', label: 'Auto' },
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
  iconSize: [
    { value: '', label: 'Auto' },
    { value: 16, label: '16px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 28, label: '28px' },
  ],
  sizePx: [
    { value: '', label: 'Auto' },
    { value: 0, label: '0px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
    { value: 128, label: '128px' },
  ],
  paddingPx: [
    { value: '', label: 'Auto' },
    { value: 0, label: '0px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 6, label: '6px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
  ],
  logoSizePx: [
    { value: '', label: 'Auto' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
    { value: 128, label: '128px' },
  ],
  confirmationMarkSize: [
    { value: '', label: 'Auto' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 56, label: '56px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
  ],
  alignment: [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ],
  radius: [
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
    { value: 'full', label: 'Full' },
  ],
  colorMode: [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ],
  headingSize: [
    { value: 'xs', label: 'Extra small' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
  bodySize: [
    { value: 'xs', label: 'Extra small' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
  indicatorShape: [
    { value: 'dot', label: 'Dot' },
    { value: 'tick', label: 'Tick' },
    { value: 'star', label: 'Star' },
  ],
  indicatorRadius: [
    { value: 0, label: 'None' },
    { value: 2, label: 'Small' },
    { value: 4, label: 'Medium' },
    { value: 8, label: 'Large' },
    { value: 9999, label: 'Full' },
  ],
  indicatorSize: [
    { value: '', label: 'Auto' },
    { value: 12, label: '12px' },
    { value: 14, label: '14px' },
    { value: 16, label: '16px' },
    { value: 18, label: '18px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 28, label: '28px' },
    { value: 32, label: '32px' },
  ],
  badgeRadius: [
    { value: '', label: 'Auto' },
    { value: 0, label: 'None' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
  ],
} as const;

export type OptionsRef = keyof typeof THEME_OPTIONS;

// ============================================================================
// Registry Entries (339 properties)
// ============================================================================

export const THEME_PROPERTY_REGISTRY: ThemeProperty[] = [
  {
    path: 'variables.tokens.colorPrimary',
    type: 'color',
    description:
      'Primary brand color used for buttons, links, and accents. Produces primary, primary-dark, logo-text, and logo-bg CSS variables.',
  },
  {
    path: 'variables.tokens.colorSecondary',
    type: 'color',
    description: 'Secondary accent color. Produces secondary and secondary-dark CSS variables.',
  },
  {
    path: 'variables.tokens.textPrimary',
    type: 'color',
    description: 'Primary text color used throughout the checkout.',
  },
  {
    path: 'variables.tokens.textSecondary',
    type: 'color',
    description: 'Secondary text color for less prominent text elements.',
  },
  {
    path: 'variables.tokens.textTertiary',
    type: 'color',
    description:
      'Supporting text color for auxiliary elements like "Pay with card" and "Payments are secure & encrypted".',
  },
  {
    path: 'variables.tokens.colorBackground',
    type: 'color',
    description: 'Decorative page chrome color. No text sits directly on this color.',
  },
  {
    path: 'variables.tokens.colorSurface',
    type: 'color',
    description: 'Neutral working surface for forms and cards.',
  },
  {
    path: 'variables.tokens.colorSurfaceAccent',
    type: 'color',
    description:
      'Branded content surface. Text sits on it. Used for the double layout content panel by default.',
  },
  {
    path: 'variables.tokens.radius',
    type: 'select',
    description:
      'Global border radius token. Produces 4 CSS variables for checkout, card, input, and button border-radius.',
    options: 'radius' as OptionsRef,
  },
  {
    path: 'variables.tokens.colors',
    type: 'select',
    description: 'Color mode for the checkout: light, dark, or system preference.',
    options: 'colorMode' as OptionsRef,
  },
  {
    path: 'variables.locale',
    type: 'select',
    description: 'Locale used for currency and number formatting.',
  },
  {
    path: 'variables.tokens.fontMain.family',
    type: 'font',
    description:
      'Base font family inherited by both Title and Text fonts when they are set to Auto.',
  },
  {
    path: 'variables.tokens.fontHeading.family',
    type: 'font',
    description: 'Font family for all heading text. Applied with fallback chain.',
  },
  {
    path: 'variables.tokens.fontHeading.size',
    type: 'select',
    description: 'Heading size preset. Produces h1-h6 font-size and line-height CSS variables.',
    options: 'headingSize' as OptionsRef,
  },
  {
    path: 'variables.tokens.fontHeading.weight',
    type: 'fontWeight',
    description: 'Font weight for all headings.',
  },
  {
    path: 'variables.tokens.fontBody.family',
    type: 'font',
    description: 'Font family for body text. Applied with fallback chain.',
  },
  {
    path: 'variables.tokens.fontBody.size',
    type: 'select',
    description:
      'Body text size preset. Produces xs/sm/base/lg font-size and line-height CSS variables.',
    options: 'bodySize' as OptionsRef,
  },
  {
    path: 'variables.tokens.fontBody.weight',
    type: 'fontWeight',
    description: 'Font weight for body text. Also derives label weight (+100).',
  },
  // Per-layout color overrides
  {
    path: 'variables.layout.centerLayout.backgroundColor',
    type: 'color',
    description: 'Page background color override for center layout.',
  },
  {
    path: 'variables.layout.centerLayout.contentBackgroundColor',
    type: 'color',
    description: 'Content background color override for center layout.',
  },
  {
    path: 'variables.layout.centerLayout.formBackgroundColor',
    type: 'color',
    description: 'Form background color override for center layout.',
  },
  {
    path: 'variables.layout.centerLayout.backgroundImage',
    type: 'image',
    description: 'Background image for center layout.',
  },
  {
    path: 'variables.layout.centerLayout.backgroundPosition',
    type: 'select',
    description: 'Background image position for center layout.',
    options: [
      { value: 'center', label: 'Center' },
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' },
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.layout.doubleLayout.contentBackgroundColor',
    type: 'color',
    description: 'Content background color override for double layout.',
  },
  {
    path: 'variables.layout.doubleLayout.formBackgroundColor',
    type: 'color',
    description: 'Form background color override for double layout.',
  },
  {
    path: 'variables.layout.splitLayout.backgroundColor',
    type: 'color',
    description: 'Page background color override for split layout.',
  },
  {
    path: 'variables.layout.splitLayout.contentBackgroundColor',
    type: 'color',
    description: 'Content background color override for split layout.',
  },
  {
    path: 'variables.layout.splitLayout.formBackgroundColor',
    type: 'color',
    description: 'Form background color override for split layout.',
  },
  {
    path: 'variables.layout.splitLayout.backgroundImage',
    type: 'image',
    description: 'Background image for split layout.',
  },
  {
    path: 'variables.layout.splitLayout.backgroundPosition',
    type: 'select',
    description: 'Background image position for split layout.',
    options: [
      { value: 'center', label: 'Center' },
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' },
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.layout.shadowColor',
    type: 'color',
    description: 'Color of the checkout container shadow.',
  },
  {
    path: 'variables.layout.shadowWidth',
    type: 'shadow',
    description: 'Size of the checkout container shadow.',
    options: 'shadow' as OptionsRef,
  },
  {
    path: 'variables.layout.doubleLayout.formPosition',
    type: 'select',
    description: 'Position of the form in double column layout.',
    options: [
      { value: 'right', label: 'Content left · Form right' },
      { value: 'left', label: 'Form left · Content right' },
    ],
  },
  {
    path: 'variables.layout.doubleLayout.backgroundWidth',
    type: 'select',
    description: 'Content background width in double column layout.',
    options: [
      { value: 'half', label: 'Half' },
      { value: 'full', label: 'Full' },
    ],
  },
  {
    path: 'variables.layout.splitLayout.mainColumn',
    type: 'select',
    description: 'Which side the main column appears on in split layout.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.elements.container.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the outer container.',
  },
  {
    path: 'variables.elements.container.styles.color',
    type: 'color',
    description: 'Text color of the outer container.',
  },
  {
    path: 'variables.elements.product.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the product section.',
  },
  {
    path: 'variables.elements.product.styles.color',
    type: 'color',
    description: 'Default text color of the product section.',
  },
  {
    path: 'variables.elements.product.styles.textAlign',
    type: 'alignment',
    description: 'Text alignment for the product section.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.elements.product.title.styles.color',
    type: 'color',
    description: 'Color of the product title text.',
  },
  {
    path: 'variables.elements.product.title.styles.fontFamily',
    type: 'font',
    description: 'Font family for the product title in the product section.',
  },
  {
    path: 'variables.elements.product.title.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the product title in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.title.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the product title in the product section.',
  },
  {
    path: 'variables.elements.product.title.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the product title in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.price.styles.color',
    type: 'color',
    description: 'Color of the product price.',
  },
  {
    path: 'variables.elements.product.pricing.price.styles.fontFamily',
    type: 'font',
    description: 'Font family for the product price in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.price.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the product price in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.price.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the product price in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.price.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the product price in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.interval.styles.color',
    type: 'color',
    description: 'Color of the subscription interval (e.g. "/ month").',
  },
  {
    path: 'variables.elements.product.pricing.interval.styles.fontFamily',
    type: 'font',
    description: 'Font family for the subscription interval.',
  },
  {
    path: 'variables.elements.product.pricing.interval.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the subscription interval.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.interval.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the subscription interval.',
  },
  {
    path: 'variables.elements.product.pricing.interval.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the subscription interval.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.color',
    type: 'color',
    description: 'Color of the original price shown with a discount.',
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.fontFamily',
    type: 'font',
    description: 'Font family for the discounted from price in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the discounted from price in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the discounted from price in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the discounted from price in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.color',
    type: 'color',
    description: 'Color of the trial period text.',
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.fontFamily',
    type: 'font',
    description: 'Font family for the trial period in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the trial period in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the trial period in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the trial period in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.startDate.styles.color',
    type: 'color',
    description: 'Color of the subscription start date text.',
  },
  {
    path: 'variables.elements.product.pricing.startDate.styles.fontFamily',
    type: 'font',
    description: 'Font family for the start date in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.startDate.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the start date in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.startDate.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the start date in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.startDate.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the start date in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.planIterations.styles.color',
    type: 'color',
    description: 'Color of the number of payments text.',
  },
  {
    path: 'variables.elements.product.pricing.planIterations.styles.fontFamily',
    type: 'font',
    description: 'Font family for the plan iterations in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.planIterations.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the plan iterations in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.planIterations.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the plan iterations in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.planIterations.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the plan iterations in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.color',
    type: 'color',
    description: 'Color of the proration renewal date text.',
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.fontFamily',
    type: 'font',
    description: 'Font family for the proration renews in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the proration renews in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the proration renews in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the proration renews in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.color',
    type: 'color',
    description: 'Color of the proration amount text.',
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.fontFamily',
    type: 'font',
    description: 'Font family for the proration amount in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the proration amount in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the proration amount in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the proration amount in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.setupFee.styles.color',
    type: 'color',
    description: 'Color of the setup fee text.',
  },
  {
    path: 'variables.elements.product.pricing.setupFee.styles.fontFamily',
    type: 'font',
    description: 'Font family for the setup fee in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.setupFee.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the setup fee in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.setupFee.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the setup fee in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.setupFee.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the setup fee in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  // Single-price highlight pill (the Price element's Highlight) — independent
  // of the price-picker option badge.
  {
    path: 'variables.elements.product.pricing.badge.enabled',
    type: 'toggle',
    description: 'Whether the single-price highlight is visible.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.color',
    type: 'color',
    description: 'Text color of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderColor',
    type: 'color',
    description: 'Border color of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderWidth',
    type: 'size',
    description: 'Border width of the single-price highlight.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the single-price highlight.',
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.padding',
    type: 'size',
    description: 'Padding inside the single-price highlight.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.fontFamily',
    type: 'font',
    description: 'Font family of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the single-price highlight.',
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the single-price highlight.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.logo.hostedPageLogoStyle',
    type: 'select',
    description: 'Which logo variant to show on hosted pages.',
    options: [
      { value: 'logoBanner', label: 'Banner' },
      { value: 'logoBubble', label: 'Bubble' },
    ],
  },
  {
    path: 'variables.elements.product.logo.logoBanner.bannerStyle',
    type: 'select',
    description: 'Banner logo display variant.',
    options: [
      { value: 'full', label: 'Mark and name' },
      { value: 'icon', label: 'Mark only' },
      { value: 'name', label: 'Name only' },
    ],
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.size',
    type: 'size',
    description: 'Size of the logo mark in the banner.',
    options: 'logoSizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.color',
    type: 'color',
    description: 'Color of the logo name text in the banner.',
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.fontFamily',
    type: 'font',
    description: 'Font family for the logo banner in the product section.',
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the logo banner in the product section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the logo banner in the product section.',
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the logo banner in the product section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.logo.logoBubble.styles.size',
    type: 'size',
    description: 'Size of the logo bubble.',
    options: 'logoSizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.logo.logoBubble.styles.textAlign',
    type: 'alignment',
    description: 'Alignment of the logo bubble.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.elements.product.logo.logoBubble.styles.backgroundColor',
    type: 'color',
    description: 'Background color behind the logo bubble image.',
  },
  {
    path: 'variables.elements.product.store.logo.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the store logo.',
  },
  {
    path: 'variables.elements.product.store.logo.styles.color',
    type: 'color',
    description: 'Text color of the store logo.',
  },
  {
    path: 'variables.elements.product.store.logo.styles.textAlign',
    type: 'alignment',
    description: 'Alignment of the store logo.',
  },
  {
    path: 'variables.elements.product.store.logoWithStoreName.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the logo with store name.',
  },
  {
    path: 'variables.elements.product.store.logoWithStoreName.styles.color',
    type: 'color',
    description: 'Text color of the logo with store name.',
  },
  {
    path: 'variables.elements.product.event.date.styles.color',
    type: 'color',
    description: 'Color of the event date text.',
  },
  {
    path: 'variables.elements.product.event.location.styles.color',
    type: 'color',
    description: 'Color of the event location text.',
  },
  {
    path: 'variables.elements.product.event.virtual.styles.color',
    type: 'color',
    description: 'Color of the virtual event text.',
  },
  {
    path: 'variables.elements.description.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the description section.',
  },
  {
    path: 'variables.elements.description.headings.styles.color',
    type: 'color',
    description: 'Color of headings in the description section.',
  },
  {
    path: 'variables.elements.description.body.styles.color',
    type: 'color',
    description: 'Color of body text in the description section.',
  },
  {
    path: 'variables.elements.description.links.styles.color',
    type: 'color',
    description: 'Color of links in the description section.',
  },
  {
    path: 'variables.elements.description.quotes.styles.color',
    type: 'color',
    description: 'Color of blockquotes in the description section.',
  },
  {
    path: 'variables.elements.description.bullets.styles.color',
    type: 'color',
    description: 'Color of the marker on bulleted lists in the description.',
  },
  {
    path: 'variables.elements.description.bullets.styles.fontSize',
    type: 'size',
    description: 'Size of the marker on bulleted lists in the description.',
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  {
    path: 'variables.elements.description.bullets.align',
    type: 'select',
    description: 'Where the marker sits against the first line of a list item.',
    options: [
      { value: 'top', label: 'Top' },
      { value: 'middle', label: 'Middle' },
      { value: 'bottom', label: 'Bottom' },
    ],
  },
  {
    path: 'variables.elements.description.bullets.type',
    type: 'select',
    description: 'Marker shown on bulleted lists in the description.',
    options: [
      { value: 'dot', label: 'Dot' },
      { value: 'tick', label: 'Tick' },
      { value: 'dash', label: 'Dash' },
      { value: 'arrow', label: 'Arrow' },
    ],
  },
  {
    path: 'variables.elements.description.collapsibles.title.styles.color',
    type: 'color',
    description: 'Color of accordion titles in the description.',
  },
  {
    path: 'variables.elements.description.collapsibles.body.styles.color',
    type: 'color',
    description: 'Color of accordion body text in the description.',
  },
  {
    path: 'variables.elements.description.collapsibles.icon.styles.color',
    type: 'color',
    description: 'Color of the accordion toggle icon in the description.',
  },
  {
    path: 'variables.elements.description.collapsibles.icon.type',
    type: 'select',
    description: 'Icon shown on accordion toggles in the description.',
    options: [
      { value: 'chevron', label: 'Chevron' },
      { value: 'plus', label: 'Plus' },
      { value: 'caret', label: 'Caret' },
    ],
    defaultValue: 'chevron',
  },
  {
    path: 'variables.elements.description.collapsibles.border.enabled',
    type: 'toggle',
    description: 'Show/hide the bottom border on accordions in the description.',
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderColor',
    type: 'color',
    description: 'Color of the bottom border on accordions.',
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderWidth',
    type: 'size',
    description: 'Width of the bottom border on accordions.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderRadius',
    type: 'size',
    description: 'Border radius of accordions.',
    options: [
      { value: '', label: 'Auto' },
      { value: 0, label: '0px' },
      { value: 2, label: '2px' },
      { value: 4, label: '4px' },
      { value: 8, label: '8px' },
    ],
  },
  {
    path: 'variables.elements.checkout.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the checkout container.',
  },
  {
    path: 'variables.elements.checkout.styles.color',
    type: 'color',
    description: 'Default text color of the checkout container.',
  },
  {
    path: 'variables.elements.checkout.styles.borderColor',
    type: 'color',
    description: 'Border color of the checkout container.',
  },
  {
    path: 'variables.elements.checkout.styles.borderWidth',
    type: 'size',
    description: 'Border width of the checkout container.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the checkout container.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.styles.padding',
    type: 'size',
    description: 'Padding of the checkout container.',
    options: [
      { value: '', label: 'Auto' },
      { value: 32, label: '32px' },
      { value: 48, label: '48px' },
      { value: 64, label: '64px' },
      { value: 80, label: '80px' },
    ],
  },
  {
    path: 'variables.elements.checkout.divider.styles.borderColor',
    type: 'color',
    description: 'Color of the divider line in the checkout.',
  },
  {
    path: 'variables.elements.checkout.form.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the checkout form.',
  },
  {
    path: 'variables.elements.checkout.form.styles.color',
    type: 'color',
    description: 'Default text color of the checkout form.',
  },
  {
    path: 'variables.elements.checkout.form.fields.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the fields container.',
  },
  {
    path: 'variables.elements.checkout.form.fields.styles.color',
    type: 'color',
    description: 'Text color of the fields container.',
  },
  {
    path: 'variables.elements.checkout.form.fields.field.styles.backgroundColor',
    type: 'color',
    description: 'Background color of individual field wrappers.',
  },
  {
    path: 'variables.elements.checkout.form.fields.field.styles.color',
    type: 'color',
    description: 'Text color of individual field wrappers.',
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.color',
    type: 'color',
    description: 'Color of field labels.',
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.paddingBottom',
    type: 'size',
    description: 'Bottom padding below field labels.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.fontFamily',
    type: 'font',
    description: 'Font family for the field label in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the field label in the fields section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the field label in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.label.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the field label in the fields section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.label.focusStyles.color',
    type: 'color',
    description: 'Color of the field label when the input is focused.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.color',
    type: 'color',
    description: 'Text color of input fields.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.backgroundColor',
    type: 'color',
    description: 'Background color of input fields.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderColor',
    type: 'color',
    description: 'Border color of input fields.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderWidth',
    type: 'size',
    description: 'Border width of input fields.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderRadius',
    type: 'size',
    description: 'Border radius of input fields.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.paddingX',
    type: 'size',
    description: 'Horizontal padding of input fields.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.paddingY',
    type: 'size',
    description: 'Vertical padding of input fields.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.boxShadowColor',
    type: 'color',
    description: 'Shadow color of input fields.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.boxShadow',
    type: 'shadow',
    description: 'Shadow size of input fields.',
    options: 'shadow' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.fontFamily',
    type: 'font',
    description: 'Font family for the input field in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the input field in the fields section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the input field in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the input field in the fields section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.color',
    type: 'color',
    description:
      'Text color of input fields when focused. Only emitted when differs from input resting state.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.backgroundColor',
    type: 'color',
    description:
      'Background color of input fields when focused. Only emitted when differs from input resting state.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.borderColor',
    type: 'color',
    description:
      'Border color of input fields when focused. Only emitted when differs from input resting state.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.borderRadius',
    type: 'size',
    description: 'Border radius of input fields when focused.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.placeholder.enabled',
    type: 'toggle',
    description: 'Whether input field placeholders are visible.',
  },
  {
    path: 'variables.elements.checkout.form.fields.input.placeholderStyles.color',
    type: 'color',
    description:
      'Color of input field placeholder text. Handled by placeholder enabled toggle logic.',
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.icon.styles.color',
    type: 'color',
    description: 'Color of the − and + icons in quantity steppers.',
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.icon.styles.size',
    type: 'size',
    description: 'Size of the − and + icons in quantity steppers.',
    options: 'iconSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.backgroundColor',
    type: 'color',
    description: 'Background of the quantity stepper count box.',
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.color',
    type: 'color',
    description: 'Text color of the quantity stepper count box.',
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderColor',
    type: 'color',
    description: 'Border color of the quantity stepper count box.',
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderWidth',
    type: 'size',
    description: 'Border width of the quantity stepper count box.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the quantity stepper count box.',
    options: 'sizePx' as OptionsRef,
  },

  {
    path: 'variables.elements.checkout.form.fields.description.styles.color',
    type: 'color',
    description: 'Color of field descriptions.',
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.paddingTop',
    type: 'size',
    description: 'Top padding above field descriptions.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the field description in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the field description in the fields section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the field description in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the field description in the fields section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.groups.styles.gap',
    type: 'size',
    description: 'Space between fields that sit side by side in a group.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.groups.styles.rowGap',
    type: 'size',
    description: 'Space between rows of fields in a group.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.groups.phones',
    type: 'select',
    description: 'Whether fields in a group stack or stay side by side on narrow screens.',
    options: [
      { value: 'stack', label: 'Stack fields' },
      { value: 'keep', label: 'Keep side by side' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.groups.styles.alignItems',
    type: 'select',
    description: 'How fields of different heights line up in a row of a group.',
    options: [
      { value: 'start', label: 'Top' },
      { value: 'center', label: 'Center' },
      { value: 'end', label: 'Bottom' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.color',
    type: 'color',
    description: 'Color of headings in the form.',
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.paddingBottom',
    type: 'size',
    description: 'Space below headings in the form.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.fontFamily',
    type: 'font',
    description: 'Font family for the form heading in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the form heading in the fields section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the form heading in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the form heading in the fields section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.color',
    type: 'color',
    description: 'Color of subheadings in the form.',
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.paddingBottom',
    type: 'size',
    description: 'Space below subheadings in the form.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.fontFamily',
    type: 'font',
    description: 'Font family for the form subheading in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the form subheading in the fields section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the form subheading in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the form subheading in the fields section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.borderColor',
    type: 'color',
    description: 'Color of dividers in the form.',
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.borderWidth',
    type: 'size',
    description: 'Thickness of dividers in the form.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.borderStyle',
    type: 'select',
    description: 'Line style of dividers in the form.',
    options: 'borderStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.width',
    type: 'select',
    description: 'How far dividers reach across the form.',
    options: [
      { value: '', label: 'Auto' },
      { value: '100%', label: 'Full width' },
      { value: '50%', label: 'Half width' },
      { value: '4rem', label: 'Short' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.marginTop',
    type: 'size',
    description: 'Space above dividers in the form.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.marginBottom',
    type: 'size',
    description: 'Space below dividers in the form.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.color',
    type: 'color',
    description: 'Text color of the submit button.',
  },
  {
    path: 'variables.elements.checkout.form.button.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the submit button.',
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderColor',
    type: 'color',
    description: 'Border color of the submit button.',
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderWidth',
    type: 'size',
    description: 'Border width of the submit button.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the submit button.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.padding',
    type: 'size',
    description: 'Padding of the submit button.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.fontFamily',
    type: 'font',
    description: 'Font family for the submit button in the button section.',
  },
  {
    path: 'variables.elements.checkout.form.button.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the submit button in the button section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the submit button in the button section.',
  },
  {
    path: 'variables.elements.checkout.form.button.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the submit button in the button section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.color',
    type: 'color',
    description: 'Text color of the submit button on hover.',
  },
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the submit button on hover.',
  },
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.borderColor',
    type: 'color',
    description: 'Border color of the submit button on hover.',
  },
  // --- Cards (shared defaults) ---
  {
    path: 'variables.elements.checkout.cards.styles.backgroundColor',
    type: 'color',
    description: 'Default background color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderColor',
    type: 'color',
    description: 'Default border color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderWidth',
    type: 'size',
    description: 'Default border width for selectable cards.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderRadius',
    type: 'size',
    description: 'Default border radius for selectable cards.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Default background color for selected cards.',
  },
  {
    path: 'variables.elements.checkout.cards.selectedStyles.borderColor',
    type: 'color',
    description: 'Default border color for selected cards.',
  },
  {
    path: 'variables.elements.checkout.cards.detail.styles.color',
    type: 'color',
    description:
      'Default color for secondary text on cards (descriptions, remaining, dates, etc.).',
  },
  {
    path: 'variables.elements.checkout.cards.name.styles.color',
    type: 'color',
    description: 'Default name color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card name.',
  },
  {
    path: 'variables.elements.checkout.cards.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card name.',
  },
  {
    path: 'variables.elements.checkout.cards.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card name.',
  },
  {
    path: 'variables.elements.checkout.cards.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card name.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.description.styles.color',
    type: 'color',
    description: 'Default description color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card description.',
  },
  {
    path: 'variables.elements.checkout.cards.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card description.',
  },
  {
    path: 'variables.elements.checkout.cards.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card description.',
  },
  {
    path: 'variables.elements.checkout.cards.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card description.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.price.styles.color',
    type: 'color',
    description: 'Default price color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.price.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card price.',
  },
  {
    path: 'variables.elements.checkout.cards.price.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card price.',
  },
  {
    path: 'variables.elements.checkout.cards.price.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card price.',
  },
  {
    path: 'variables.elements.checkout.cards.price.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card price.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.color',
    type: 'color',
    description: 'Default discounted from price color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card discounted from price.',
  },
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card discounted from price.',
  },
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card discounted from price.',
  },
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card discounted from price.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.backgroundColor',
    type: 'color',
    description: 'Default group name background color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.color',
    type: 'color',
    description: 'Default group name color for selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.paddingBottom',
    type: 'size',
    description: 'Default bottom padding below card group names.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card group name.',
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card group name.',
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card group name.',
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card group name.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.color',
    type: 'color',
    description: 'Default text color of the highlight pill on selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.backgroundColor',
    type: 'color',
    description: 'Default background color of the highlight pill on selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderColor',
    type: 'color',
    description: 'Default border color of the highlight pill on selectable cards.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderWidth',
    type: 'size',
    description: 'Default border width of the highlight pill on selectable cards.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderRadius',
    type: 'size',
    description: 'Default border radius of the highlight pill on selectable cards.',
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.padding',
    type: 'size',
    description: 'Default padding inside the highlight pill on selectable cards.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.fontFamily',
    type: 'font',
    description: 'Font family for the card highlight.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the card highlight.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the card highlight.',
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the card highlight.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.position',
    type: 'select',
    description: 'Where product variants are displayed.',
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.backgroundColor',
    type: 'color',
    description: 'Background color of variant option cards.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderColor',
    type: 'color',
    description: 'Border color of variant option cards.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderWidth',
    type: 'size',
    description: 'Border width of variant option cards.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderRadius',
    type: 'size',
    description: 'Border radius of variant option cards.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected variant option.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected variant option.',
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the variant group name.',
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.color',
    type: 'color',
    description: 'Color of the variant group name.',
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.paddingBottom',
    type: 'size',
    description: 'Bottom padding below the variant group name.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant name in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant name in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant name in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant name in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the variant option name.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.color',
    type: 'color',
    description: 'Color of the variant option name.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant option name in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant option name in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant option name in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant option name in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the variant option description.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.color',
    type: 'color',
    description: 'Color of the variant option description.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant option description in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant option description in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant option description in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant option description in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the variant option price.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.color',
    type: 'color',
    description: 'Color of the variant option price.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant option price in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant option price in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant option price in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant option price in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.color',
    type: 'color',
    description: 'Color of the original price on variant options.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant discounted from price in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant discounted from price in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant discounted from price in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant discounted from price in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.color',
    type: 'color',
    description: 'Color of the plan iterations text on variant options.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.fontFamily',
    type: 'font',
    description: 'Font family for the variant plan iterations in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the variant plan iterations in the variants section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the variant plan iterations in the variants section.',
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the variant plan iterations in the variants section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.plusSign.styles.color',
    type: 'color',
    description: 'Color of the plus sign between variant options.',
  },
  // --- Variant options pwywInput (the variant-option pay-what-you-want amount box) ---
  ...pwywInputApiEntries('variables.elements.checkout.variants.variantOptions.pwywInput'),
  {
    path: 'variables.elements.checkout.variants.variantOptions.pwywSuffix.enabled',
    type: 'toggle',
    description: 'Whether the variant PWYW interval / currency suffix is visible.',
  },
  ...pwywSuffixApiEntries('variables.elements.checkout.variants.variantOptions.pwywSuffix'),
  {
    path: 'variables.elements.checkout.ticketGroups.position',
    type: 'select',
    description: 'Where ticket types are displayed.',
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.quantitySelector',
    type: 'select',
    description: 'How buyers pick a ticket count: a dropdown or a − / + stepper.',
    options: [
      { value: 'dropdown', label: 'Dropdown' },
      { value: 'stepper', label: 'Stepper' },
    ],
  },

  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.backgroundColor',
    type: 'color',
    description: 'Background color of ticket type cards.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderColor',
    type: 'color',
    description: 'Border color of ticket type cards.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderWidth',
    type: 'size',
    description: 'Border width of ticket type cards.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderRadius',
    type: 'size',
    description: 'Border radius of ticket type cards.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected ticket type.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected ticket type.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the ticket group name.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.color',
    type: 'color',
    description: 'Color of the ticket group name.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.paddingBottom',
    type: 'size',
    description: 'Bottom padding below the ticket group name.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket group name in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket group name in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket group name in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket group name in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the ticket group description.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.color',
    type: 'color',
    description: 'Color of the ticket group description.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket group description in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket group description in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket group description in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket group description in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.color',
    type: 'color',
    description: 'Color of the ticket type name.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type name in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type name in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type name in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type name in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.color',
    type: 'color',
    description: 'Color of the ticket type description.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type description in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type description in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type description in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type description in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.color',
    type: 'color',
    description: 'Color of the ticket type price.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type price in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type price in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type price in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type price in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.color',
    type: 'color',
    description: 'Color of the original price on ticket types.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.fontFamily',
    type: 'font',
    description:
      'Font family for the ticket type discounted from price in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.fontStyle',
    type: 'fontStyle',
    description:
      'Font style for the ticket type discounted from price in the ticketGroups section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.fontWeight',
    type: 'fontWeight',
    description:
      'Font weight for the ticket type discounted from price in the ticketGroups section.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type discounted from price in the ticketGroups section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.color',
    type: 'color',
    description: 'Color of the booking fee note under a ticket price.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type booking fee note.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type booking fee note.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type booking fee note.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type booking fee note.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.color',
    type: 'color',
    description: 'Color of the ticket type remaining count.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type remaining text.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type remaining text.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type remaining text.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type remaining text.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.color',
    type: 'color',
    description: 'Color of the ticket type sale date.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type sale date.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type sale date.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type sale date.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type sale date.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.color',
    type: 'color',
    description: 'Color of the ticket type total price.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.fontFamily',
    type: 'font',
    description: 'Font family for the ticket type total price.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the ticket type total price.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the ticket type total price.',
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the ticket type total price.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.discountCode.link.styles.color',
    type: 'color',
    description: 'Color of the discount code link.',
  },
  {
    path: 'variables.elements.checkout.form.discountCode.applyButton.styles.color',
    type: 'color',
    description: 'Color of the discount code apply button.',
  },
  {
    path: 'variables.elements.checkout.form.discountCode.code.styles.color',
    type: 'color',
    description: 'Text color of the applied discount code.',
  },
  {
    path: 'variables.elements.checkout.form.discountCode.code.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the applied discount code.',
  },
  {
    path: 'variables.elements.checkout.form.discountCode.discount.styles.color',
    type: 'color',
    description: 'Color of the discount amount text.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.backgroundColor',
    type: 'color',
    description: 'Background color of manual payment options.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.color',
    type: 'color',
    description: 'Text color of manual payment options.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderColor',
    type: 'color',
    description: 'Border color of manual payment options.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderWidth',
    type: 'size',
    description: 'Border width of manual payment options.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderRadius',
    type: 'size',
    description: 'Border radius of manual payment options.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.padding',
    type: 'size',
    description: 'Padding of manual payment options.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected manual payment option.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected manual payment option.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.color',
    type: 'color',
    description: 'Text color of the selected manual payment option.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.borderWidth',
    type: 'size',
    description: 'Border width of the selected manual payment option.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.color',
    type: 'color',
    description: 'Color of the manual payment name.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.fontFamily',
    type: 'font',
    description: 'Font family for the manual payment name in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the manual payment name in the payments section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the manual payment name in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the manual payment name in the payments section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.color',
    type: 'color',
    description: 'Color of the manual payment description.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.fontFamily',
    type: 'font',
    description: 'Font family for the manual payment description in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the manual payment description in the payments section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the manual payment description in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the manual payment description in the payments section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.color',
    type: 'color',
    description: 'Color of the manual payment instructions.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.fontFamily',
    type: 'font',
    description: 'Font family for the manual payment instructions in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the manual payment instructions in the payments section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the manual payment instructions in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the manual payment instructions in the payments section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the price breakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.color',
    type: 'color',
    description: 'Default text color of the price breakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.fontFamily',
    type: 'font',
    description: 'Font family for the price breakdown in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the price breakdown in the priceBreakdown section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the price breakdown in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the price breakdown in the priceBreakdown section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.alignment',
    type: 'alignment',
    description: 'Text alignment of the price breakdown. Maps to flexbox justify/align values.',
    options: [
      { value: '', label: 'Auto' },
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
      { value: 'justify', label: 'Justify' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.subtotal.styles.color',
    type: 'color',
    description: 'Color of the subtotal line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.orderBump.styles.color',
    type: 'color',
    description: 'Color of the order bump lines.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticket.styles.color',
    type: 'color',
    description: 'Color of the per-ticket lines shown when a ticket carries a fee.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticketFee.styles.color',
    type: 'color',
    description: 'Color of the booking fee lines.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.setupFee.styles.color',
    type: 'color',
    description: 'Color of the setup fee line.',
  },

  {
    path: 'variables.elements.checkout.form.priceBreakdown.bulkDiscount.styles.color',
    type: 'color',
    description: 'Color of the bulk discount line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.couponDiscount.styles.color',
    type: 'color',
    description: 'Color of the coupon discount line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.fees.styles.color',
    type: 'color',
    description: 'Color of the fees line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.processingFeeContribution.styles.color',
    type: 'color',
    description: 'Color of the processing fee contribution line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.tax.styles.color',
    type: 'color',
    description: 'Color of the tax line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.startDate.styles.color',
    type: 'color',
    description: 'Color of the start date line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.trial.styles.color',
    type: 'color',
    description: 'Color of the trial line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.dueNow.styles.color',
    type: 'color',
    description: 'Color of the due now line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.proration.styles.color',
    type: 'color',
    description: 'Color of the proration line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.color',
    type: 'color',
    description: 'Color of the total line.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.fontFamily',
    type: 'font',
    description: 'Font family for the price breakdown total in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the price breakdown total in the priceBreakdown section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the price breakdown total in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the price breakdown total in the priceBreakdown section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.selector',
    type: 'select',
    description: 'What the customer ticks to cover processing fees.',
    options: [
      { value: 'checkbox', label: 'Checkbox' },
      { value: 'switch', label: 'Switch' },
      { value: 'mark', label: 'Mark' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the cover processing fees box.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the cover processing fees box once ticked.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderColor',
    type: 'color',
    description: 'Border color of the cover processing fees box.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the cover processing fees box once ticked.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderWidth',
    type: 'size',
    description: 'Border width of the cover processing fees box.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the cover processing fees box.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.size',
    type: 'size',
    description: 'Size of the cover processing fees checkbox.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.backgroundColor',
    type: 'color',
    description: 'Background of the cover processing fees checkbox.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background of the cover processing fees checkbox once ticked.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderColor',
    type: 'color',
    description: 'Border color of the cover processing fees checkbox.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the cover processing fees checkbox once ticked.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the cover processing fees checkbox.',
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.color',
    type: 'color',
    description: 'Color of the tick inside a ticked cover processing fees checkbox.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.size',
    type: 'size',
    description: 'Height of the cover processing fees switch; its track is 1.75 times as wide.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.backgroundColor',
    type: 'color',
    description: 'Track color of the cover processing fees switch while it is off.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Track color of the cover processing fees switch once it is on.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.color',
    type: 'color',
    description: 'Color of the knob that slides along the cover processing fees switch.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.shape',
    type: 'select',
    description: 'Shape of the cover processing fees selected mark (dot, tick or star).',
    options: 'indicatorShape' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.size',
    type: 'size',
    description: 'Size of the cover processing fees mark.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the cover processing fees mark (9999 = circle, 0 = square).',
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderWidth',
    type: 'size',
    description: 'Border width of the cover processing fees mark.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.backgroundColor',
    type: 'color',
    description: 'Background of an unselected cover processing fees mark.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background of a selected cover processing fees mark.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderColor',
    type: 'color',
    description: 'Border color of an unselected cover processing fees mark.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of a selected cover processing fees mark.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.color',
    type: 'color',
    description: 'Color of the dot, tick or star inside a selected cover processing fees mark.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.color',
    type: 'color',
    description: 'Color of the cover processing fees label.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.fontFamily',
    type: 'font',
    description:
      'Font family for the cover processing fees label in the coverProcessingFees section.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.fontStyle',
    type: 'fontStyle',
    description:
      'Font style for the cover processing fees label in the coverProcessingFees section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.fontWeight',
    type: 'fontWeight',
    description:
      'Font weight for the cover processing fees label in the coverProcessingFees section.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.fontSize',
    type: 'fontSize',
    description:
      'Font size for the cover processing fees label in the coverProcessingFees section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.color',
    type: 'color',
    description: 'Color of the amount beside the cover processing fees label.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.fontFamily',
    type: 'font',
    description:
      'Font family for the cover processing fees amount in the coverProcessingFees section.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.fontStyle',
    type: 'fontStyle',
    description:
      'Font style for the cover processing fees amount in the coverProcessingFees section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.fontWeight',
    type: 'fontWeight',
    description:
      'Font weight for the cover processing fees amount in the coverProcessingFees section.',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.fontSize',
    type: 'fontSize',
    description:
      'Font size for the cover processing fees amount in the coverProcessingFees section.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.position',
    type: 'select',
    description: 'Where order bumps are displayed.',
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.layout',
    type: 'select',
    description: 'Order bump cards stacked down the page, or side by side in a grid.',
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid-2', label: '2 columns' },
      { value: 'grid-3', label: '3 columns' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptAppearance',
    type: 'select',
    description:
      'What the call to action sits on: the offer itself, a boxed row of its own, or a bar flush with the sides of the card.',
    options: [
      { value: 'bar', label: 'Bar' },
      { value: 'row', label: 'Row' },
      { value: 'offer', label: 'Beside the offer' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptPosition',
    type: 'select',
    description: 'Where the call to action sits on the card.',
    options: [
      { value: 'top-right', label: 'Top right' },
      { value: 'top-left', label: 'Top left' },
      { value: 'top-center', label: 'Top center' },
      { value: 'center-right', label: 'Center right' },
      { value: 'center-left', label: 'Center left' },
      { value: 'bottom-right', label: 'Bottom right' },
      { value: 'bottom-left', label: 'Bottom left' },
      { value: 'bottom-center', label: 'Bottom center' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptStyle',
    type: 'select',
    description: 'What the buyer taps to accept the offer.',
    options: [
      { value: 'switch', label: 'Switch' },
      { value: 'mark', label: 'Mark' },
      { value: 'button', label: 'Button' },
      { value: 'link', label: 'Link' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.fill',
    type: 'select',
    description:
      'Whether the order bump accept button is filled in the Button color or drawn as an outline in it.',
    options: [
      { value: 'solid', label: 'Solid' },
      { value: 'outline', label: 'Outline' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.width',
    type: 'select',
    description: 'Whether the order bump accept button fits its text or fills its line.',
    options: [
      { value: 'fit', label: 'Fit to text' },
      { value: 'full', label: 'Full width' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.shape',
    type: 'select',
    description: 'Shape of the order bump selected mark (dot, tick or star).',
    options: 'indicatorShape' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the order bump mark (9999 = circle, 0 = square).',
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.size',
    type: 'size',
    description: 'Size of the order bump mark.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderWidth',
    type: 'size',
    description: 'Border width of the order bump mark.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.backgroundColor',
    type: 'color',
    description: 'Background of an unselected order bump mark.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background of a selected order bump mark.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.color',
    type: 'color',
    description: 'Color of the dot, tick or star inside a selected order bump mark.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderColor',
    type: 'color',
    description: 'Border color of an unselected order bump mark.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of a selected order bump mark.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.size',
    type: 'size',
    description: 'Height of the order bump switch; its track is 1.75 times as wide.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.backgroundColor',
    type: 'color',
    description: 'Track color of the order bump switch when the bump is not in the order.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Track color of the order bump switch once the bump is in the order.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.color',
    type: 'color',
    description: 'Color of the knob that slides along the order bump switch.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.color',
    type: 'color',
    description:
      'Text color of the order bump accept button. Unset, a solid button follows the Button element, or takes black or white on a background of its own, and an outline button follows its border.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.color',
    type: 'color',
    description:
      'Text color of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.backgroundColor',
    type: 'color',
    description:
      'Background of the order bump accept button. Unset, it follows the Button element.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.backgroundColor',
    type: 'color',
    description:
      'Background of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderColor',
    type: 'color',
    description:
      'Border color of the order bump accept button. Unset, it follows the Button element.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.borderColor',
    type: 'color',
    description:
      'Border color of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderWidth',
    type: 'size',
    description:
      'Border width of the order bump accept button. Unset, it follows the Button element.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderRadius',
    type: 'size',
    description:
      'Border radius of the order bump accept button. Unset, it follows the Button element.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingX',
    type: 'size',
    description: 'Horizontal padding of the order bump accept button.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingY',
    type: 'size',
    description: 'Vertical padding of the order bump accept button.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.link.styles.color',
    type: 'color',
    description:
      'Text color of the order bump accept link. Unset, it follows the Call to action element.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.link.selectedStyles.color',
    type: 'color',
    description:
      'Text color of the order bump accept link once the bump is in the order. Unset, a shade darker than the resting text color.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.link.styles.textDecoration',
    type: 'select',
    description: 'Whether the order bump accept link is underlined.',
    options: [
      { value: 'underline', label: 'Underline' },
      { value: 'none', label: 'None' },
    ],
  },
  ...fontProps('variables.elements.checkout.form.orderBumps.link.styles', 'order bump link'),
  {
    path: 'variables.elements.checkout.form.orderBumps.mediaPosition',
    type: 'select',
    description: 'Where the offer thumbnail sits on the card.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
      { value: 'top', label: 'Top' },
    ],
    legacyValues: ['none'],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.horizontalAlign',
    type: 'select',
    description: 'Horizontal alignment of the order bump card content.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.descriptionPosition',
    type: 'select',
    description:
      'Whether the features, description and call out run the full card width or stay inset in the text column, clearing the image beside it.',
    options: [
      { value: 'column', label: 'Indented' },
      { value: 'full', label: 'Full width' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.position',
    type: 'select',
    description:
      'Whether the order bump highlight sits above the title or at the end of the title row.',
    options: [
      { value: 'above', label: 'Above title' },
      { value: 'inline', label: 'Beside title' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the order bump accept row.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the order bump accept row once the bump is selected.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the order bump accept row once the bump is selected.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderColor',
    type: 'color',
    description: 'Border color of the order bump accept row.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderWidth',
    type: 'size',
    description: 'Border width of the order bump accept row.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the order bump accept row.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.padding',
    type: 'size',
    description: 'Inner padding of the order bump accept row.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.dividerWidth',
    type: 'size',
    description: 'Width of the rule between the order bump accept row and the offer.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.backgroundColor',
    type: 'color',
    description: 'Background color of order bump cards.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderColor',
    type: 'color',
    description: 'Border color of order bump cards.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderWidth',
    type: 'size',
    description: 'Border width of order bump cards.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderStyle',
    type: 'select',
    description: 'Border style of order bump cards.',
    options: 'borderStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderRadius',
    type: 'size',
    description: 'Border radius of order bump cards.',
    options: 'sizePx' as OptionsRef,
  },
  // --- Order bump pwywInput (the pay-what-you-want amount box on a bump) ---
  ...pwywInputApiEntries('variables.elements.checkout.form.orderBumps.pwywInput'),
  {
    path: 'variables.elements.checkout.form.orderBumps.pwywSuffix.enabled',
    type: 'toggle',
    description: 'Whether the order bump PWYW interval / currency suffix is visible.',
  },
  ...pwywSuffixApiEntries('variables.elements.checkout.form.orderBumps.pwywSuffix'),
  {
    path: 'variables.elements.checkout.form.orderBumps.image.styles.size',
    type: 'select',
    description:
      'Size of the thumbnail on an order bump card. Full width spans the card, so it reads best with the image above the copy.',
    options: [
      { value: 64, label: 'Small' },
      { value: 128, label: 'Medium' },
      { value: 192, label: 'Large' },
      { value: 'full', label: 'Full width' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.image.styles.aspectRatio',
    type: 'select',
    description:
      'Shape of the thumbnail on an order bump card. Original keeps the picture as it was uploaded.',
    options: [
      { value: 'square', label: 'Square' },
      { value: 'landscape', label: 'Landscape (4:3)' },
      { value: 'wide', label: 'Wide (16:9)' },
      { value: 'original', label: 'Original' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.image.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the thumbnail on an order bump card.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of a selected order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of a selected order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.heading.styles.color',
    type: 'color',
    description: 'Color of the heading above an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.heading.styles.paddingBottom',
    type: 'size',
    description: 'Space between the heading and the order bump card beneath it.',
    options: 'paddingPx' as OptionsRef,
  },
  ...fontProps('variables.elements.checkout.form.orderBumps.heading.styles', 'order bump heading'),
  {
    path: 'variables.elements.checkout.form.orderBumps.features.layout',
    type: 'select',
    description:
      'Whether the order bump features run down the card as a list or across it in two columns, dropping to one where the card is narrow.',
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid', label: 'Grid' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.styles.color',
    type: 'color',
    description: 'Text color of the features list on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.styles.gap',
    type: 'size',
    description: 'Space between the lines of the features list on an order bump card.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.icon.styles.color',
    type: 'color',
    description: 'Color of the icon leading each line of the features list on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.icon.styles.size',
    type: 'size',
    description: 'Size of the icon leading each line of the features list on an order bump card.',
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontProps(
    'variables.elements.checkout.form.orderBumps.features.styles',
    'order bump features',
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.color',
    type: 'color',
    description: 'Text color of the call out box on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the call out box on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderColor',
    type: 'color',
    description: 'Border color of the call out box on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderWidth',
    type: 'size',
    description: 'Border width of the call out box on an order bump card.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the call out box on an order bump card.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.padding',
    type: 'size',
    description: 'Padding inside the call out box on an order bump card.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.icon.styles.color',
    type: 'color',
    description: 'Color of the icon leading the call out box on an order bump card.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.icon.styles.size',
    type: 'size',
    description: 'Size of the icon leading the call out box on an order bump card.',
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontProps('variables.elements.checkout.form.orderBumps.callout.styles', 'order bump call out'),
  {
    path: 'variables.elements.checkout.form.orderBumps.title.styles.color',
    type: 'color',
    description: 'Color of the order bump section heading and card titles.',
  },
  ...fontProps('variables.elements.checkout.form.orderBumps.title.styles', 'order bump title'),
  {
    path: 'variables.elements.checkout.form.orderBumps.shortDescription.styles.color',
    type: 'color',
    description: 'Color of the single-line description under an order bump title.',
  },
  ...fontProps(
    'variables.elements.checkout.form.orderBumps.shortDescription.styles',
    'order bump short description',
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.callToAction.styles.color',
    type: 'color',
    description: 'Color of the call-to-action line on an order bump card.',
  },
  ...fontProps(
    'variables.elements.checkout.form.orderBumps.callToAction.styles',
    'order bump call to action',
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.price.styles.color',
    type: 'color',
    description: 'Color of the order bump card price.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.enabled',
    type: 'toggle',
    description: 'Whether the order bump highlight is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.color',
    type: 'color',
    description: 'Text color of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderColor',
    type: 'color',
    description: 'Border color of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderWidth',
    type: 'size',
    description: 'Border width of the order bump highlight.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the order bump highlight.',
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.padding',
    type: 'size',
    description: 'Padding inside the order bump highlight.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.fontFamily',
    type: 'font',
    description: 'Font family of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the order bump highlight.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.icon.styles.color',
    type: 'color',
    description: 'Color of the icon leading the order bump highlight.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.icon.styles.size',
    type: 'size',
    description: 'Size of the icon leading the order bump highlight.',
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontProps('variables.elements.checkout.form.orderBumps.price.styles', 'order bump price'),
  {
    path: 'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles.color',
    type: 'color',
    description: 'Color of the struck-through original price on an order bump.',
  },
  ...fontProps(
    'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles',
    'order bump discounted from price',
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.setupFee.styles.color',
    type: 'color',
    description: 'Color of the setup fee line on an order bump.',
  },
  ...fontProps(
    'variables.elements.checkout.form.orderBumps.setupFee.styles',
    'order bump setup fee',
  ),
  {
    path: 'variables.elements.checkout.form.orPayWithCard.styles.color',
    type: 'color',
    description: 'Text color of the "or pay with card" divider.',
  },
  {
    path: 'variables.elements.checkout.form.orPayWithCard.styles.borderColor',
    type: 'color',
    description: 'Border color of the "or pay with card" divider.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the secure payments badge.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.color',
    type: 'color',
    description: 'Text color of the secure payments badge.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.fontFamily',
    type: 'font',
    description: 'Font family for the secure payments badge in the trust section.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for the secure payments badge in the trust section.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for the secure payments badge in the trust section.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for the secure payments badge in the trust section.',
    options: 'fontSize' as OptionsRef,
  },
  // Status badges
  {
    path: 'variables.elements.checkout.statusBadges.styles.fontFamily',
    type: 'font',
    description: 'Font family for status badges.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style for status badges.',
    options: 'fontStyle' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.statusBadges.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight for status badges.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.styles.fontSize',
    type: 'fontSize',
    description: 'Font size for status badges.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.statusBadges.soldOutColor',
    type: 'color',
    description: 'Text color of the sold out badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.soldOutBackgroundColor',
    type: 'color',
    description: 'Background color of the sold out badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.canceledColor',
    type: 'color',
    description: 'Text color of the canceled badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.canceledBackgroundColor',
    type: 'color',
    description: 'Background color of the canceled badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.unavailableColor',
    type: 'color',
    description: 'Text color of the unavailable badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.unavailableBackgroundColor',
    type: 'color',
    description: 'Background color of the unavailable badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.onSaleSoonColor',
    type: 'color',
    description: 'Text color of the on sale soon badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.onSaleSoonBackgroundColor',
    type: 'color',
    description: 'Background color of the on sale soon badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.notOnSaleColor',
    type: 'color',
    description: 'Text color of the not on sale badge.',
  },
  {
    path: 'variables.elements.checkout.statusBadges.notOnSaleBackgroundColor',
    type: 'color',
    description: 'Background color of the not on sale badge.',
  },
  {
    path: 'variables.elements.poweredByStripe.styles.badgeStyle',
    type: 'select',
    description: 'Style variant of the Powered by Stripe badge.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'outline_black', label: 'Black' },
      { value: 'outline_white', label: 'White' },
    ],
  },
  {
    path: 'variables.elements.checkout.confirmation.mark.styles.color',
    type: 'color',
    description: 'Color of the confirmation check icon.',
  },
  {
    path: 'variables.elements.checkout.confirmation.mark.styles.size',
    type: 'size',
    description: 'Size of the confirmation check icon.',
    options: 'confirmationMarkSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.confirmation.heading.styles.color',
    type: 'color',
    description: 'Color of the confirmation heading text.',
  },
  {
    path: 'variables.elements.checkout.confirmation.text.styles.color',
    type: 'color',
    description: 'Color of the confirmation message text.',
  },
  {
    path: 'variables.elements.product.logo.enabled',
    type: 'toggle',
    description: 'Whether the logo is visible in the product section.',
  },
  {
    path: 'variables.elements.product.title.enabled',
    type: 'toggle',
    description: 'Whether the product title is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.enabled',
    type: 'toggle',
    description: 'Whether the pricing is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.price.enabled',
    type: 'toggle',
    description: 'Whether the price is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.interval.enabled',
    type: 'toggle',
    description: 'Whether the subscription interval is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.enabled',
    type: 'toggle',
    description: 'Whether the discounted from price is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.setupFee.enabled',
    type: 'toggle',
    description: 'Whether the setup fee is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.trialPeriod.enabled',
    type: 'toggle',
    description: 'Whether the trial period is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.startDate.enabled',
    type: 'toggle',
    description: 'Whether the start date is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.planIterations.enabled',
    type: 'toggle',
    description: 'Whether the number of payments is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationRenews.enabled',
    type: 'toggle',
    description: 'Whether the proration renews is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricing.prorationAmount.enabled',
    type: 'toggle',
    description: 'Whether the proration amount is visible in the product section.',
  },
  {
    path: 'variables.elements.product.event.date.enabled',
    type: 'toggle',
    description: 'Whether the event date is visible in the product section.',
  },
  {
    path: 'variables.elements.product.event.location.enabled',
    type: 'toggle',
    description: 'Whether the event location is visible in the product section.',
  },
  {
    path: 'variables.elements.product.event.virtual.enabled',
    type: 'toggle',
    description: 'Whether the virtual event is visible in the product section.',
  },
  {
    path: 'variables.elements.checkout.divider.enabled',
    type: 'toggle',
    description: 'Whether the divider is visible in the checkout section.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.enabled',
    type: 'toggle',
    description: 'Whether the order bumps is visible in the checkout section.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callToAction.enabled',
    type: 'toggle',
    description: 'Whether the order bump call to action is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.image.enabled',
    type: 'toggle',
    description: 'Whether the order bump image is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.heading.enabled',
    type: 'toggle',
    description: 'Whether the heading above an order bump card is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.enabled',
    type: 'toggle',
    description: 'Whether the features list on an order bump card is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.enabled',
    type: 'toggle',
    description: 'Whether the call out box on an order bump card is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.title.enabled',
    type: 'toggle',
    description: 'Whether the order bump title is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.shortDescription.enabled',
    type: 'toggle',
    description: 'Whether the order bump short description is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.price.enabled',
    type: 'toggle',
    description: 'Whether the order bump price is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.discountedFromPrice.enabled',
    type: 'toggle',
    description: 'Whether the order bump discounted-from price is visible.',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.setupFee.enabled',
    type: 'toggle',
    description: 'Whether the order bump setup fee is visible.',
  },
  {
    path: 'variables.elements.checkout.form.fields.label.enabled',
    type: 'toggle',
    description: 'Whether the field labels is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.heading.enabled',
    type: 'toggle',
    description: 'Whether the form headings is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.subheading.enabled',
    type: 'toggle',
    description: 'Whether the form subheadings is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.divider.enabled',
    type: 'toggle',
    description: 'Whether the form dividers is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.fields.description.enabled',
    type: 'toggle',
    description: 'Whether the field descriptions is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.pwyw.enabled',
    type: 'toggle',
    description: 'Whether the pay what you want section is visible in the fields section.',
  },
  {
    path: 'variables.elements.checkout.form.pwyw.minPrice.enabled',
    type: 'toggle',
    description: 'Whether the PWYW minimum price is visible in the fields section.',
  },
  // --- Base "Pay what you want" Label + Input (variables.elements.checkout.pwyw.*) ---
  // These drive all three pay-what-you-want inputs (single-price, multi-price
  // option, product variant) until a per-context subsection override is set.
  // Mirrors packages/checkout/src/theming/themePropertyRegistry.ts PWYW_ENTRIES.
  {
    path: 'variables.elements.checkout.pwyw.label.enabled',
    type: 'toggle',
    description: 'Whether the pay-what-you-want label is visible.',
  },
  ...pwywLabelApiEntries('variables.elements.checkout.pwyw.label'),
  ...pwywInputApiEntries('variables.elements.checkout.pwyw.input'),
  {
    path: 'variables.elements.checkout.pwyw.suffix.enabled',
    type: 'toggle',
    description: 'Whether the pay-what-you-want interval / currency suffix is visible.',
  },
  ...pwywSuffixApiEntries('variables.elements.checkout.pwyw.suffix'),
  {
    path: 'variables.elements.checkout.pwyw.position',
    type: 'select',
    description: 'Where the single-price pay-what-you-want input is displayed.',
    options: [
      { value: 'content-top', label: 'Content top' },
      { value: 'content-bottom', label: 'Content bottom' },
      { value: 'form-top', label: 'Form top' },
      { value: 'form-bottom', label: 'Form bottom' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.button.priceOnButton.enabled',
    type: 'toggle',
    description: 'Whether the price on button is visible in the button section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.name.enabled',
    type: 'toggle',
    description: 'Whether the manual payment name is visible in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.description.enabled',
    type: 'toggle',
    description: 'Whether the manual payment description is visible in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.enabled',
    type: 'toggle',
    description: 'Whether the manual payment instructions is visible in the payments section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.enabled',
    type: 'toggle',
    description: 'Whether the price breakdown is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.subtotal.enabled',
    type: 'toggle',
    description: 'Whether the subtotal is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.setupFee.enabled',
    type: 'toggle',
    description: 'Whether the setup fee is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.bulkDiscount.enabled',
    type: 'toggle',
    description: 'Whether the bulk discount is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.couponDiscount.enabled',
    type: 'toggle',
    description: 'Whether the coupon discount is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.orderBump.enabled',
    type: 'toggle',
    description: 'Whether the order bump lines is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticket.enabled',
    type: 'toggle',
    description: 'Whether the ticket lines is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticketFee.enabled',
    type: 'toggle',
    description: 'Whether the booking fee lines is visible in the priceBreakdown section.',
  },

  {
    path: 'variables.elements.checkout.form.priceBreakdown.tax.enabled',
    type: 'toggle',
    description: 'Whether the tax is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.startDate.enabled',
    type: 'toggle',
    description: 'Whether the start date is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.trial.enabled',
    type: 'toggle',
    description: 'Whether the trial is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.fees.enabled',
    type: 'toggle',
    description: 'Whether the fees is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.enabled',
    type: 'toggle',
    description: 'Whether the total is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.dueNow.enabled',
    type: 'toggle',
    description: 'Whether the due now is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.proration.enabled',
    type: 'toggle',
    description: 'Whether the proration is visible in the priceBreakdown section.',
  },
  {
    path: 'variables.elements.checkout.form.securePayments.enabled',
    type: 'toggle',
    description: 'Whether the secure payments is visible in the trust section.',
  },
  {
    path: 'variables.elements.checkout.form.orPayWithCard.enabled',
    type: 'toggle',
    description: 'Whether the "or pay with card" is visible in the trust section.',
  },
  {
    path: 'variables.elements.poweredByStripe.enabled',
    type: 'toggle',
    description: 'Whether the Powered by Stripe badge is visible in the trust section.',
  },
  {
    path: 'variables.elements.poweredByCheckoutPage.enabled',
    type: 'toggle',
    description: 'Whether the Powered by Checkout Page badge is shown on the checkout.',
  },
  {
    path: 'variables.elements.checkout.form.stripeCardElement.input.styles.color',
    type: 'color',
    description: 'Text color of the Stripe card input field.',
  },
  {
    path: 'variables.customCss',
    type: 'text',
    description: 'Custom CSS to inject into the checkout.',
  },

  // --- Product price picker ---
  {
    path: 'variables.elements.product.pricePicker.enabled',
    type: 'toggle',
    description: 'Whether the multi-price options (picker) are visible.',
  },
  // Price-options per-line + structural visibility toggles.
  {
    path: 'variables.elements.product.pricePicker.tabs.enabled',
    type: 'toggle',
    description: 'Whether the price grouping tabs are visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.enabled',
    type: 'toggle',
    description: 'Whether the price option cards are visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.price.enabled',
    type: 'toggle',
    description: 'Whether the price is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.interval.enabled',
    type: 'toggle',
    description: 'Whether the subscription interval is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.discountedFromPrice.enabled',
    type: 'toggle',
    description: 'Whether the compare-at price is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.setupFee.enabled',
    type: 'toggle',
    description: 'Whether the setup fee is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.trialPeriod.enabled',
    type: 'toggle',
    description: 'Whether the trial period is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.startDate.enabled',
    type: 'toggle',
    description: 'Whether the start date is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.planIterations.enabled',
    type: 'toggle',
    description: 'Whether the number of payments is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.prorationRenews.enabled',
    type: 'toggle',
    description: 'Whether the proration renews line is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.prorationAmount.enabled',
    type: 'toggle',
    description: 'Whether the proration amount line is visible in each price option.',
  },
  {
    path: 'variables.elements.product.pricePicker.position',
    type: 'select',
    description: 'Where the price options are displayed.',
    options: [
      { value: 'content', label: 'Content' },
      { value: 'form', label: 'Form' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.groupBy',
    type: 'select',
    description: 'How price options are grouped into tabs.',
    defaultValue: 'auto',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'none', label: 'No grouping' },
      { value: 'billing', label: 'By billing model' },
      { value: 'interval', label: 'By interval' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.groupingMode',
    type: 'select',
    description: 'Direction of the grouping tabs bar.',
    options: [
      { value: 'horizontal', label: 'Horizontal' },
      { value: 'vertical', label: 'Vertical' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.layout',
    type: 'select',
    description: 'Price options as a list or a grid.',
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid-2', label: '2 columns' },
      { value: 'grid-3', label: '3 columns' },
      { value: 'grid-4', label: '4 columns' },
      { value: 'grid-5', label: '5 columns' },
      { value: 'grid-6', label: '6 columns' },
      { value: 'grid-7', label: '7 columns' },
      { value: 'grid-8', label: '8 columns' },
      { value: 'grid-9', label: '9 columns' },
      { value: 'grid-10', label: '10 columns' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.horizontalAlign',
    type: 'select',
    description: 'Horizontal alignment of the price option content.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.verticalAlign',
    type: 'select',
    description: 'Vertical alignment of the price option content.',
    options: [
      { value: 'top', label: 'Top' },
      { value: 'center', label: 'Center' },
      { value: 'bottom', label: 'Bottom' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.pwywDisplay',
    type: 'select',
    description: 'How pay-what-you-want prices are displayed in the price list.',
    options: [
      { value: 'inline', label: 'Inline input' },
      { value: 'button', label: 'Button' },
      { value: 'standalone', label: 'Standalone input' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the price grouping tabs bar.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected grouping tab.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderColor',
    type: 'color',
    description: 'Border color of the price grouping tabs bar.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected grouping tab.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderWidth',
    type: 'size',
    description: 'Border width of the price grouping tabs bar.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the price grouping tabs bar.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.padding',
    type: 'size',
    description: 'Padding inside the price grouping tabs bar.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.paddingBottom',
    type: 'size',
    description: 'Space between the grouping tabs and the price options.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.color',
    type: 'color',
    description: 'Text color of the price grouping tab titles.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.fontFamily',
    type: 'font',
    description: 'Font family of the price grouping tab titles.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the price grouping tab titles.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the price grouping tab titles.',
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the price grouping tab titles.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.title.enabled',
    type: 'toggle',
    description: 'Whether the heading above the price options is visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.color',
    type: 'color',
    description: 'Text color of the heading above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.fontFamily',
    type: 'font',
    description: 'Font family of the heading above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the heading above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the heading above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the heading above the price options.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.paddingBottom',
    type: 'size',
    description: 'Space between the heading (and its description) and the price options.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.description.enabled',
    type: 'toggle',
    description: 'Whether the description above the price options is visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.description.styles.color',
    type: 'color',
    description: 'Text color of the description above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.description.styles.fontFamily',
    type: 'font',
    description: 'Font family of the description above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the description above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the description above the price options.',
  },
  {
    path: 'variables.elements.product.pricePicker.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the description above the price options.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.styles.gap',
    type: 'size',
    description: 'Gap between price picker options.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.backgroundColor',
    type: 'color',
    description: 'Background color of price picker options.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderColor',
    type: 'color',
    description: 'Border color of price picker options.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderWidth',
    type: 'size',
    description: 'Border width of price picker options.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderRadius',
    type: 'size',
    description: 'Border radius of price picker options.',
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.padding',
    type: 'size',
    description: 'Padding inside price picker options.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.color',
    type: 'color',
    description: 'Text color of all price option text (except the highlight).',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.fontFamily',
    type: 'font',
    description: 'Font family of all price option text (except the highlight).',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of all price option text (except the highlight).',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of all price option text (except the highlight).',
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of all price option text (except the highlight).',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected price picker option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected price picker option.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.enabled',
    type: 'toggle',
    description: 'Whether the price picker selection indicator is visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.color',
    type: 'color',
    description: 'Color of the selected mark (dot, tick or star).',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the price picker indicator circle.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderColor',
    type: 'color',
    description: 'Border color of the price picker indicator circle.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.size',
    type: 'size',
    description: 'Width and height of the price picker selection indicator.',
    options: 'indicatorSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderWidth',
    type: 'size',
    description: 'Border width of the price picker indicator ring.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the price picker indicator ring (9999 = circle, 0 = square).',
    options: 'indicatorRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.shape',
    type: 'select',
    description: 'Shape of the selected price picker mark (dot, tick or star).',
    options: 'indicatorShape' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.selectedStyles.backgroundColor',
    type: 'color',
    description: 'Background color of the selected price picker indicator circle.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.selectedStyles.borderColor',
    type: 'color',
    description: 'Border color of the selected price picker indicator circle.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.enabled',
    type: 'toggle',
    description: 'Whether the price badge is visible.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.color',
    type: 'color',
    description: 'Text color of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.backgroundColor',
    type: 'color',
    description: 'Background color of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderColor',
    type: 'color',
    description: 'Border color of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderWidth',
    type: 'size',
    description: 'Border width of the price badge.',
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderRadius',
    type: 'size',
    description: 'Border radius of the price badge.',
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.padding',
    type: 'size',
    description: 'Padding inside the price badge.',
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.fontFamily',
    type: 'font',
    description: 'Font family of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the price badge.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the price badge.',
    options: 'fontSize' as OptionsRef,
  },
  ...pwywInputApiEntries('variables.elements.product.pricePicker.option.pwywInput'),
  // Theme-only (no enabled toggle of its own — visibility follows the
  // existing Price options toggles); chains its defaults to the base PWYW
  // label above.
  ...pwywLabelApiEntries('variables.elements.product.pricePicker.option.pwywLabel'),
  {
    path: 'variables.elements.product.pricePicker.option.pwywSuffix.enabled',
    type: 'toggle',
    description: 'Whether the price option PWYW interval / currency suffix is visible.',
  },
  ...pwywSuffixApiEntries('variables.elements.product.pricePicker.option.pwywSuffix'),
  {
    path: 'variables.elements.product.pricePicker.option.label.enabled',
    type: 'toggle',
    description: 'Whether the price label is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.label.styles.color',
    type: 'color',
    description: 'Text color of the price option label.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.label.styles.fontFamily',
    type: 'font',
    description: 'Font family of the price option label.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.label.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the price option label.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.label.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the price option label.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.label.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the price option label.',
    options: 'fontSize' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.enabled',
    type: 'toggle',
    description: 'Whether the price description is visible in the product section.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.styles.color',
    type: 'color',
    description: 'Text color of the price option description.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.styles.fontFamily',
    type: 'font',
    description: 'Font family of the price option description.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.styles.fontStyle',
    type: 'fontStyle',
    description: 'Font style of the price option description.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.styles.fontWeight',
    type: 'fontWeight',
    description: 'Font weight of the price option description.',
  },
  {
    path: 'variables.elements.product.pricePicker.option.description.styles.fontSize',
    type: 'fontSize',
    description: 'Font size of the price option description.',
    options: 'fontSize' as OptionsRef,
  },

  // Price options — per-option price + detail lines (separate from the
  // single-price "Prices" element product.pricing.*).
  ...optionLineProps('price', 'price option price'),
  ...optionLineProps('interval', 'price option interval'),
  ...optionLineProps('discountedFromPrice', 'price option compare-at price'),
  ...optionLineProps('setupFee', 'price option setup fee'),
  ...optionLineProps('startDate', 'price option start date'),
  ...optionLineProps('planIterations', 'price option number of payments'),
  ...optionLineProps('trialPeriod', 'price option trial period'),
  ...optionLineProps('prorationRenews', 'price option proration renews'),
  ...optionLineProps('prorationAmount', 'price option proration amount'),

  // Label entries
  // Keep defaults in sync with packages/checkout/src/theming/themePropertyRegistry.ts
  {
    path: 'variables.labels.soldByText',
    type: 'text',
    description: 'Label for the "sold by" credit.',
    defaultValue: 'Sold by',
  },
  {
    path: 'variables.labels.intervalCountText',
    type: 'text',
    description: 'Prefix for multi-interval subscriptions.',
    defaultValue: 'every',
  },
  {
    path: 'variables.labels.dayText',
    type: 'text',
    description: 'Label for the "day" interval.',
    defaultValue: 'day',
  },
  {
    path: 'variables.labels.weekText',
    type: 'text',
    description: 'Label for the "week" interval.',
    defaultValue: 'week',
  },
  {
    path: 'variables.labels.monthText',
    type: 'text',
    description: 'Label for the "month" interval.',
    defaultValue: 'month',
  },
  {
    path: 'variables.labels.yearText',
    type: 'text',
    description: 'Label for the "year" interval.',
    defaultValue: 'year',
  },
  {
    path: 'variables.labels.recurringText',
    type: 'text',
    description:
      'Fallback interval label on a recurring variant option when no specific cadence applies.',
    defaultValue: 'recurring',
  },
  {
    path: 'variables.labels.trialDaysFreeText',
    type: 'text',
    description: 'Label for trial days.',
    defaultValue: 'days free',
  },
  {
    path: 'variables.labels.trialThenText',
    type: 'text',
    description: 'Label between trial and price.',
    defaultValue: 'then',
  },
  {
    path: 'variables.labels.setupFeeText',
    type: 'text',
    description: 'Label for the setup fee on the product card.',
    defaultValue: 'setup fee',
  },
  {
    path: 'variables.labels.cartSetupFeeText',
    type: 'text',
    description: 'Label for the setup fee in the price breakdown.',
    defaultValue: 'Setup fee',
  },
  {
    path: 'variables.labels.startsOnText',
    type: 'text',
    description: 'Label for subscription start date.',
    defaultValue: 'Starts on',
  },
  {
    path: 'variables.labels.renewsOnText',
    type: 'text',
    description: 'Label for subscription renewal date.',
    defaultValue: 'Renews on',
  },
  {
    path: 'variables.labels.todayText',
    type: 'text',
    description: 'Label for "today" date display.',
    defaultValue: 'Today',
  },
  {
    path: 'variables.labels.proratedAmountText',
    type: 'text',
    description: 'Label for prorated amount display.',
    defaultValue: '(prorated amount)',
  },
  {
    path: 'variables.labels.endsAfterText',
    type: 'text',
    description: 'Label for subscription end.',
    defaultValue: 'Ends after',
  },
  {
    path: 'variables.labels.endsAfterPaymentsText',
    type: 'text',
    description: 'Label for payment count.',
    defaultValue: 'payments',
  },
  {
    path: 'variables.labels.variantOptionTotalText',
    type: 'text',
    description: 'Label for variant option total.',
    defaultValue: 'Total',
  },
  {
    path: 'variables.labels.ticketsRemainingText',
    type: 'text',
    description: 'Label for remaining ticket count.',
    defaultValue: 'remaining',
  },
  {
    path: 'variables.labels.ticketTypeTotalText',
    type: 'text',
    description: 'Label for ticket type total.',
    defaultValue: 'Total',
  },
  {
    path: 'variables.labels.bookingFeeText',
    type: 'text',
    description: 'Name of the per-ticket booking fee, on the ticket and in the price breakdown.',
    defaultValue: 'Booking fee',
  },
  {
    path: 'variables.labels.ticketFeeIncludesText',
    type: 'text',
    description:
      'Leads the note under a ticket price that includes its fee, e.g. "Includes $3 booking fee".',
    defaultValue: 'Includes',
  },

  {
    path: 'variables.labels.canceledText',
    type: 'text',
    description: 'Label for canceled status.',
    defaultValue: 'Canceled',
  },
  {
    path: 'variables.labels.canceledButtonText',
    type: 'text',
    description: 'Label for canceled button.',
    defaultValue: 'Canceled',
  },
  {
    path: 'variables.labels.unavailableText',
    type: 'text',
    description: 'Label for unavailable status.',
    defaultValue: 'Unavailable',
  },
  {
    path: 'variables.labels.onSaleSoonText',
    type: 'text',
    description: 'Label for upcoming sale status.',
    defaultValue: 'On sale soon',
  },
  {
    path: 'variables.labels.notOnSaleText',
    type: 'text',
    description: 'Label for not-on-sale status.',
    defaultValue: 'Not on sale',
  },
  {
    path: 'variables.labels.startDateText',
    type: 'text',
    description: 'Label for event start date.',
    defaultValue: 'Starts',
  },
  {
    path: 'variables.labels.endDateText',
    type: 'text',
    description: 'Label for event end date.',
    defaultValue: 'Ends',
  },
  {
    path: 'variables.labels.endedAtDateText',
    type: 'text',
    description: 'Label for past event end date.',
    defaultValue: 'Ended on',
  },
  {
    path: 'variables.labels.virtualEventText',
    type: 'text',
    description: 'Label for virtual event type.',
    defaultValue: 'Virtual event',
  },
  {
    path: 'variables.labels.hybridEventText',
    type: 'text',
    description: 'Label for hybrid event type.',
    defaultValue: 'Hybrid event',
  },
  {
    path: 'variables.labels.soldOutText',
    type: 'text',
    description: 'Label for sold out status.',
    defaultValue: 'Sold out',
  },
  {
    path: 'variables.labels.soldOutButtonText',
    type: 'text',
    description: 'Label for sold out button.',
    defaultValue: 'Sold out',
  },
  {
    path: 'variables.labels.customerInformationText',
    type: 'text',
    description: 'Label for customer information section.',
    defaultValue: 'Customer information',
  },
  {
    path: 'variables.labels.emailSuggestionText',
    type: 'text',
    description: 'Label for email typo suggestion.',
    defaultValue: 'Did you mean',
  },
  {
    path: 'variables.labels.addDiscountCodeText',
    type: 'text',
    description: 'Label for add discount code link.',
    defaultValue: 'Add discount code',
  },
  {
    path: 'variables.labels.discountCodeText',
    type: 'text',
    description: 'Label for discount code field.',
    defaultValue: 'Discount code',
  },
  {
    path: 'variables.labels.discountCodePlaceholderText',
    type: 'text',
    description: 'Placeholder for discount code input.',
    defaultValue: 'Discount code',
  },
  {
    path: 'variables.labels.discountCodeApplyText',
    type: 'text',
    description: 'Label for discount code apply button.',
    defaultValue: 'Apply',
  },
  {
    path: 'variables.labels.payWhatYouWantText',
    type: 'text',
    description: 'Label for pay what you want field.',
    defaultValue: 'Pay what you want',
  },
  {
    path: 'variables.labels.payWhatYouWantPlaceholderText',
    type: 'text',
    description: 'Placeholder for pay what you want input.',
    defaultValue: 'Pay what you want',
  },
  {
    path: 'variables.labels.pwywMinPricePlaceholderText',
    type: 'text',
    description: 'Placeholder for PWYW minimum price input.',
    defaultValue: 'Min.',
  },
  {
    path: 'variables.labels.pwywSuggestedPricePlaceholderText',
    type: 'text',
    description: 'Placeholder for PWYW suggested price input.',
    defaultValue: 'Suggested',
  },
  {
    path: 'variables.labels.ticketPwywMinPricePlaceholderText',
    type: 'text',
    description:
      'Placeholder label for the pay-what-you-want ticket amount input when no suggested price is set.',
    defaultValue: 'Min.',
  },
  {
    path: 'variables.labels.ticketPwywSuggestedPricePlaceholderText',
    type: 'text',
    description:
      'Placeholder label for the pay-what-you-want ticket amount input when a suggested price is set.',
    defaultValue: 'Suggested',
  },
  {
    path: 'variables.labels.ticketPwywEnterAmountPlaceholderText',
    type: 'text',
    description:
      'Placeholder for the pay-what-you-want ticket amount input when neither a minimum nor a suggested price is set.',
    defaultValue: 'Enter amount',
  },
  {
    path: 'variables.labels.pwywEnterAmountText',
    type: 'text',
    description: 'Label for PWYW options without a minimum or suggested price.',
    defaultValue: 'Enter amount',
  },
  {
    path: 'variables.labels.orPayWithCardText',
    type: 'text',
    description: 'Label for alternative card payment option.',
    defaultValue: 'Or pay with card',
  },
  {
    path: 'variables.labels.cardLabelText',
    type: 'text',
    description: 'Label for card payment field.',
    defaultValue: 'Card',
  },
  {
    path: 'variables.labels.variantIsRequiredText',
    type: 'text',
    description: 'Validation message for required variants.',
    defaultValue: 'is required',
  },
  {
    path: 'variables.labels.cartSubtotalText',
    type: 'text',
    description: 'Label for subtotal in price breakdown.',
    defaultValue: 'Subtotal',
  },
  {
    path: 'variables.labels.orderBumpsTitleText',
    type: 'text',
    description: 'Heading above the order bump cards.',
    defaultValue: 'Add to your order',
  },
  {
    path: 'variables.labels.orderBumpCallToActionText',
    type: 'text',
    description: 'Button or link copy on an order bump that has none of its own.',
    defaultValue: 'Add',
  },
  {
    path: 'variables.labels.orderBumpShowMoreText',
    type: 'text',
    description: 'Toggle that expands a collapsed order bump description.',
    defaultValue: 'Show more',
  },
  {
    path: 'variables.labels.orderBumpShowLessText',
    type: 'text',
    description: 'Toggle that collapses an expanded order bump description.',
    defaultValue: 'Show less',
  },
  {
    path: 'variables.labels.showMoreText',
    type: 'text',
    description: 'Toggle that expands a description cut to its first lines.',
    defaultValue: 'Show more',
  },
  {
    path: 'variables.labels.showLessText',
    type: 'text',
    description: 'Toggle that collapses an expanded description.',
    defaultValue: 'Show less',
  },
  {
    path: 'variables.labels.cartBulkDiscountText',
    type: 'text',
    description: 'Label for bulk discount in price breakdown.',
    defaultValue: 'Bulk discount',
  },
  {
    path: 'variables.labels.cartCouponDiscountText',
    type: 'text',
    description: 'Label for coupon discount in price breakdown.',
    defaultValue: 'Coupon discount',
  },
  {
    path: 'variables.labels.cartTaxText',
    type: 'text',
    description: 'Label for tax in price breakdown.',
    defaultValue: 'Tax',
  },
  {
    path: 'variables.labels.cartTaxIncludedText',
    type: 'text',
    description: 'Leads the tax line when prices include tax, e.g. "Includes VAT (20%)".',
    defaultValue: 'Includes',
  },
  {
    path: 'variables.labels.processingFeeContributionText',
    type: 'text',
    description:
      'Name of the processing fee contribution line in the price breakdown, added when the customer ticks to cover processing fees.',
    defaultValue: 'Processing fee contribution',
  },
  {
    path: 'variables.labels.cartTotalOnStartDateText',
    type: 'text',
    description: 'Label for total on start date in price breakdown.',
    defaultValue: 'Total on',
  },
  {
    path: 'variables.labels.cartTotalAfterTrialText',
    type: 'text',
    description: 'Label for total after trial in price breakdown.',
    defaultValue: 'Total after trial',
  },
  {
    path: 'variables.labels.cartDueNowText',
    type: 'text',
    description: 'Label for due now in price breakdown.',
    defaultValue: 'Due now',
  },
  {
    path: 'variables.labels.cartTotalDueText',
    type: 'text',
    description: 'Label for total due in price breakdown.',
    defaultValue: 'Total due',
  },
  {
    path: 'variables.labels.cartTotalDueProratedAmountText',
    type: 'text',
    description: 'Label for prorated total due in price breakdown.',
    defaultValue: 'Total due now (prorated)',
  },
  {
    path: 'variables.labels.payButtonText',
    type: 'text',
    description: 'Label for pay button.',
    defaultValue: 'Pay',
  },
  {
    path: 'variables.labels.subscribeButtonText',
    type: 'text',
    description: 'Label for subscribe button.',
    defaultValue: 'Subscribe',
  },
  {
    path: 'variables.labels.submitButtonText',
    type: 'text',
    description: 'Label for submit button.',
    defaultValue: 'Submit',
  },
  {
    path: 'variables.labels.getTicketsButtonText',
    type: 'text',
    description: 'Label for get tickets button.',
    defaultValue: 'Get tickets',
  },
  {
    path: 'variables.labels.securePaymentsText',
    type: 'text',
    description: 'Label for secure payments trust badge.',
    defaultValue: 'Payments are secure & encrypted',
  },
  {
    path: 'variables.labels.scanToOpenText',
    type: 'text',
    description: 'Label for QR pages that open a form.',
    defaultValue: 'Scan to open',
  },
  {
    path: 'variables.labels.scanToPayText',
    type: 'text',
    description: 'Label for QR pages that open a payment page.',
    defaultValue: 'Scan to pay',
  },
  {
    path: 'variables.labels.upsellNoThanksText',
    type: 'text',
    description: 'Label for upsell decline link.',
    defaultValue: "No thanks, I don't want this",
  },
  {
    path: 'variables.labels.processingPaymentText',
    type: 'text',
    description: 'Label shown while processing payment.',
    defaultValue: 'Processing payment',
  },
  {
    path: 'variables.labels.viewPaymentHistoryText',
    type: 'text',
    description: 'Label for payment history link.',
    defaultValue: 'View payment history',
  },
  {
    path: 'variables.labels.viewSubmissionHistoryText',
    type: 'text',
    description: 'Label for submission history link.',
    defaultValue: 'View submission history',
  },
  {
    path: 'variables.labels.viewDownloadsText',
    type: 'text',
    description: 'Label for downloads link.',
    defaultValue: 'View downloads',
  },
  {
    path: 'variables.labels.ticketsPurchasedText',
    type: 'text',
    description: 'Label for tickets purchased confirmation.',
    defaultValue: 'Tickets purchased',
  },
  {
    path: 'variables.labels.addToCalendarText',
    type: 'text',
    description: 'Label for the add to calendar button on the event confirmation.',
    defaultValue: 'Add to calendar',
  },
];

/**
 * Get all label defaults as a { labelKey: defaultValue } map.
 * This is the single source of truth for label defaults on the backend.
 * Keep in sync with packages/checkout/src/theming/themePropertyRegistry.ts
 */
const _labelDefaults: Record<string, string> = (() => {
  const defaults: Record<string, string> = {};
  for (const prop of THEME_PROPERTY_REGISTRY) {
    if (prop.path.startsWith('variables.labels.') && prop.defaultValue != null) {
      const key = prop.path.split('.').pop()!;
      defaults[key] = prop.defaultValue as string;
    }
  }
  return Object.freeze(defaults);
})();

export function getThemeLabelDefaults(): Readonly<Record<string, string>> {
  return _labelDefaults;
}
